// Processos e restrições junto de toda consulta (2026-10-10) — o backend
// chama 5 bases extras (processos, mandados de prisão, dívida ativa PGFN,
// protestos e certidão do CNJ) e grava em `result.legal`. O formato de cada
// uma nunca foi visto com dado real: o veredito é heurístico e os dados
// completos ficam sempre visíveis. Usa as classes cx-* de routes/consultas.tsx.
import { useState, type ReactNode } from "react";
import { ChevronDown, Scale } from "lucide-react";
import { cn } from "@/lib/utils";
import { buildProfile, type ProfileProcess, type ProfileRecord } from "./profile";

export type LegalCheckKey = "processos" | "mandados" | "pgfn" | "protestos" | "cnj";
interface LegalCheck {
  status: "ok" | "error";
  message?: string;
  data?: unknown;
}
export interface LegalChecks {
  fetchedAt: string;
  checks: Partial<Record<LegalCheckKey, LegalCheck>>;
}

export const LEGAL_TITLES: Record<LegalCheckKey, string> = {
  processos: "Processos judiciais",
  mandados: "Mandados de prisão (BNMP)",
  pgfn: "Dívida ativa da União (PGFN)",
  protestos: "Protestos em cartório",
  cnj: "Improbidade e inelegibilidade (CNJ)",
};
export const LEGAL_ORDER: LegalCheckKey[] = ["processos", "mandados", "pgfn", "protestos", "cnj"];

export function legalOf(result: unknown): LegalChecks | null {
  const l = result && typeof result === "object" ? (result as { legal?: unknown }).legal : null;
  return l && typeof l === "object" && "checks" in l ? (l as LegalChecks) : null;
}

const isEmpty = (v: unknown): boolean =>
  v == null ||
  v === "" ||
  (Array.isArray(v) && v.every(isEmpty)) ||
  (typeof v === "object" && !Array.isArray(v) && Object.values(v as object).every(isEmpty));

// Alguma lista com objetos dentro = há registros.
function hasRows(v: unknown, depth = 0): boolean {
  if (depth > 8 || !v || typeof v !== "object") return false;
  if (Array.isArray(v)) return v.some((i) => i && typeof i === "object" && !isEmpty(i)) || v.some((i) => hasRows(i, depth + 1));
  return Object.values(v).some((i) => hasRows(i, depth + 1));
}

const text = (v: unknown) =>
  JSON.stringify(v ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

function verdict(key: LegalCheckKey, c: LegalCheck | undefined, processes: number): ProfileRecord["status"] {
  if (!c || c.status !== "ok") return "info";
  if (key === "processos") return processes ? "consta" : isEmpty(c.data) ? "nada_consta" : "info";
  if (isEmpty(c.data)) return "nada_consta";
  if (hasRows(c.data)) return "consta";
  const t = text(c.data);
  if (/nada consta|nao consta|nao constam|nao (foram|foi) encontrad|nenhum|negativa|sem (registro|protesto|debito|divida|mandado)/.test(t))
    return "nada_consta";
  if (/consta|positiva|possui|encontrad/.test(t)) return "consta";
  return "info";
}

const DETAIL: Record<ProfileRecord["status"], string> = {
  nada_consta: "Nada encontrado nesta base.",
  consta: "Há registro — confira os detalhes em Processos.",
  info: "Consulta feita — confira os detalhes em Processos.",
};

/** Processos achados na base de processos (formato heurístico). */
export function legalProcesses(l: LegalChecks | null): ProfileProcess[] {
  const d = l?.checks.processos;
  return d?.status === "ok" ? buildProfile(d.data).processes : [];
}

/** Uma linha por base para o painel "Registros e antecedentes". */
export function legalRecords(l: LegalChecks | null): ProfileRecord[] {
  if (!l) return [];
  const processes = legalProcesses(l).length;
  return LEGAL_ORDER.filter((k) => k !== "processos" && l.checks[k]).map((k) => {
    const c = l.checks[k]!;
    const status = verdict(k, c, processes);
    return {
      title: LEGAL_TITLES[k],
      detail: c.status === "ok" ? DETAIL[status] : (c.message ?? "Base indisponível no momento."),
      status,
      source: "GPS Drive API",
      noPdf: true,
    };
  });
}

/** Detalhe das 5 bases, com os dados completos de cada uma. */
export function LegalChecksPanel({
  data,
  renderData,
}: {
  data: LegalChecks | null;
  renderData: (v: unknown) => ReactNode;
}) {
  if (!data)
    return (
      <div className="cx-card px-4 py-6 text-center text-xs text-slate-500">
        Consulta feita antes da busca de processos e restrições — não incluído.
      </div>
    );
  const processes = legalProcesses(data).length;
  return (
    <div className="cx-card p-4">
      <div className="cx-title mb-3">
        <Scale className="h-3.5 w-3.5" /> Processos e restrições — bases consultadas
      </div>
      <ul className="space-y-2">
        {LEGAL_ORDER.filter((k) => data.checks[k]).map((k) => (
          <CheckRow
            key={k}
            title={LEGAL_TITLES[k]}
            check={data.checks[k]!}
            status={verdict(k, data.checks[k], processes)}
            renderData={renderData}
          />
        ))}
      </ul>
    </div>
  );
}

function CheckRow({
  title,
  check,
  status,
  renderData,
}: {
  title: string;
  check: LegalCheck;
  status: ProfileRecord["status"];
  renderData: (v: unknown) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const canOpen = check.status === "ok" && !isEmpty(check.data);
  const tag =
    check.status !== "ok"
      ? { label: "Indisponível", cls: "border-amber-400/40 bg-amber-400/10 text-amber-300" }
      : status === "nada_consta"
        ? { label: "Nada consta", cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" }
        : status === "consta"
          ? { label: "Consta", cls: "border-rose-400/40 bg-rose-400/10 text-rose-300" }
          : { label: "Ver dados", cls: "border-slate-600/40 text-slate-400" };
  return (
    <li className="cx-row">
      <button
        type="button"
        disabled={!canOpen}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 p-3 text-left"
      >
        <span className="text-[13px] font-medium text-slate-100">{title}</span>
        <span className="flex shrink-0 items-center gap-2">
          <span
            className={cn(
              "rounded border px-1.5 py-px text-[10px] font-semibold uppercase tracking-[0.08em]",
              tag.cls,
            )}
          >
            {tag.label}
          </span>
          {canOpen && (
            <ChevronDown
              className={cn("h-4 w-4 text-emerald-300 transition-transform", open && "rotate-180")}
            />
          )}
        </span>
      </button>
      {check.status !== "ok" && (
        <p className="px-3 pb-3 text-xs text-slate-400">{check.message ?? "Base indisponível no momento."}</p>
      )}
      {open && (
        <div className="border-t border-emerald-400/10 p-3 text-slate-200">{renderData(check.data)}</div>
      )}
    </li>
  );
}
